import classNames from 'classnames';
import CTAPageHeading from 'components/heading/cta';
import DeleteModal from 'components/modals/delete';
import Pagination from 'components/pagination';
import React, { useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { Icon } from 'components/ui/icon';
import useAuth from 'state/auth/hooks/useAuth';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import usePayment from 'state/payment/hooks/usePayment';
import { formatToTz } from 'utils/date-format';
import { APIKeysStatusFormatter } from 'utils/status-formatter';
import CreateAPIKey from './createAPIKey';
import TableWrapper, { ColumnConfig } from 'components/table';
import { PAYMENTS } from 'state/payment/type';

const APIKeys = (): JSX.Element => {
  const [showCreateAPIKeyModal, setShowCreateAPIKeyModal] =
    useState<boolean>(false);
  const { payment, toFetchKeys, toRevokeKey } = usePayment();
  const { auth } = useAuth();
  const { isSandbox } = auth;
  const { addedKey, revokeKey, keys } = payment;
  const { organisation } = useOrganisation();
  const { settings, activeOrg } = organisation;

  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteStatement = 'Are you sure you want to delete this API key?';
  const [APIkeyId, setKeyId] = useState<string>('');

  const onDeleteKeyAction = (APIkey: string) => {
    setKeyId(APIkey);
    setDeleteModal(true);
  };
  const deleteKey = () => {
    toRevokeKey(APIkeyId);
    setDeleteModal(false);
  };

  const checkStatus = (id: string) => {
    const status = keys.items?.find((keyID) => keyID.id === id)?.status;
    if (status === 'revoked') {
      return true;
    }
    return false;
  };

  useEffect(() => {
    toFetchKeys();
  }, []);

  const prevPage = () => {
    if (keys.prev_key) {
      toFetchKeys({
        key: keys.prev_key ?? null,
      });
    }
  };

  const nextPage = () => {
    if (keys.next_key) {
      toFetchKeys({
        key: keys.next_key ?? null,
      });
    }
  };

  useEffect(() => {
    if (addedKey) {
      toFetchKeys();
    }
  }, [addedKey]);

  useEffect(() => {
    if (revokeKey) {
      toFetchKeys();
    }
  }, [revokeKey]);

  const TABLE_COLUMNS: ColumnConfig<PAYMENTS.IApiKey>[] = [
    { key: 'client_id', label: 'Client ID', sortable: true },
    {
      key: 'partner_name',
      label: 'Partner',
      sortable: true,
      renderCell: (row) => (
        <div className="capitalize">{row.partner_name || 'Self'}</div>
      ),
    },
    {
      key: 'creator_name',
      sortable: true,
      label: 'Created by',
      renderCell: (row) => <>{row.creator_name || 'NA'}</>,
    },
    {
      key: 'create_date',
      label: 'Created date',
      renderCell: (row) =>
        row.create_date
          ? formatToTz(
              row.create_date,
              settings?.timezone,
              'MMM dd yyyy, hh:mm a'
            )
          : '',
      sortable: true,
      isDate: true,
    },
    {
      key: 'status',
      label: 'Status',
      renderCell: (row) => APIKeysStatusFormatter(row.status),
    },
    {
      key: 'id',
      label: 'Actions',
      renderCell: (row) => (
        <Button
          disabled={checkStatus(row.id)}
          type="button"
          variant="link"
          size="sm"
          onClick={() => onDeleteKeyAction(row.id)}
        >
          <Icon name="trash" size={18} className="text-gray-blue-60" />
        </Button>
      ),
    },
  ];

  const heading = 'API Keys';
  const subHeading = 'These keys will allow you to authenticate API requests.';

  const CreateAPIKeyButton = (
    <Button
      size="sm"
      onClick={() => setShowCreateAPIKeyModal(true)}
      className="flex items-center h-9"
    >
      <Icon name="plus" size={18} className="flex items-center h-9 pr-1" />
      Create {isSandbox ? 'test' : ''} API key
    </Button>
  );

  return (
    <>
      <Card className="card-profile border-0">
        <CTAPageHeading heading={heading} subHeading={subHeading} cta={<></>} />
        <div
          className={classNames(
            'flex justify-center items-center py-3 px-4 rounded-sm w-full h-11 border border-gray-blue-10',
            'flex mb-3'
          )}
          style={{ backgroundColor: '#FAFAFB', height: '52px' }}
        >
          <Icon name="info" size={18} className="pr-2 text-gray-blue-60" />
          <span className={classNames('text-xs text-gray-blue-70', 'mr-auto')}>
            You are viewing {isSandbox ? 'test' : 'live'} API Keys. Toggle to
            view {isSandbox ? 'live' : 'test'} keys.
          </span>
        </div>
        <TableWrapper data={keys.items || []} columns={TABLE_COLUMNS} />
        <Pagination
          results={keys.total_count}
          prevPage={prevPage}
          nextPage={nextPage}
          prevKey={keys.prev_key ?? null}
          nextKey={keys.next_key ?? null}
        />
      </Card>
      <DeleteModal
        showModal={deleteModal}
        setShowModal={setDeleteModal}
        tag="Secret key"
        warningStatement={deleteStatement}
        action={deleteKey}
      />
      {showCreateAPIKeyModal && (
        <CreateAPIKey
          openModal={showCreateAPIKeyModal}
          setModal={setShowCreateAPIKeyModal}
          isPartner={Boolean(activeOrg?.is_partner)}
        />
      )}
    </>
  );
};

export default APIKeys;
